import { ReactNode, useEffect, useState, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { GlobalTopbar } from "./components/GlobalTopbar/GlobalTopbar";
import { IconNav } from "./components/IconNav/IconNav";
import { AgentSidebar } from "./components/AgentSidebar/AgentSidebar";
import { GuidedTour, hasTourBeenSeen } from "../components/GuidedTour/GuidedTour";
import { getShowedStartupScreen } from "../components/Utils/utils";
import { useShell } from "./context/ShellContext";
import "./theme/theme.css";
import "./ShellLayout.css";

interface ShellLayoutProps {
  children: ReactNode;
}

const MOBILE_BREAKPOINT = 768;

export function ShellLayout({ children }: ShellLayoutProps) {
  const { sidebarOpen, setSidebarOpen, sidebarWidth, setSettingsPanelOpen } = useShell();
  const location = useLocation();
  const navigate = useNavigate();
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < MOBILE_BREAKPOINT);
  const [showTour, setShowTour] = useState(false);

  // Show tour automatically for first-time users — but only after the startup screen is done
  useEffect(() => {
    if (hasTourBeenSeen()) return;

    const tryLaunchTour = () => {
      if (getShowedStartupScreen()) {
        if (window.location.pathname !== "/") navigate("/");
        setShowTour(true);
        return true;
      }
      return false;
    };

    // If startup screen was already completed, launch after a short layout delay
    const timer = setTimeout(() => {
      if (tryLaunchTour()) return;
      // Otherwise, poll briefly for the startup screen to finish (user picks a mode)
      const interval = setInterval(() => {
        if (tryLaunchTour()) clearInterval(interval);
      }, 500);
      // Stop polling after 60s as a safety net
      setTimeout(() => clearInterval(interval), 60000);
    }, 800);

    return () => clearTimeout(timer);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const closeTour = useCallback(() => {
    setShowTour(false);
    setSettingsPanelOpen(false);
  }, [setSettingsPanelOpen]);

  // Expose a global trigger so Help page or user menu can launch the tour
  useEffect(() => {
    const handler = () => {
      // Navigate to home so chat UI elements are visible behind the tour
      if (window.location.pathname !== "/") navigate("/");
      setTimeout(() => setShowTour(true), 300);
    };
    window.addEventListener("aura:start-tour", handler);
    return () => window.removeEventListener("aura:start-tour", handler);
  }, [navigate]);

  // Allow tour steps to open/close the settings panel
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      setSettingsPanelOpen(!!detail?.open);
    };
    window.addEventListener("aura:tour-settings", handler);
    return () => window.removeEventListener("aura:tour-settings", handler);
  }, [setSettingsPanelOpen]);

  // Track viewport width changes (device rotation, window resize)
  useEffect(() => {
    const handler = () => {
      const mobile = window.innerWidth < MOBILE_BREAKPOINT;
      setIsMobile(mobile);
      // Auto-close when viewport shrinks into mobile range
      if (mobile && sidebarOpen) setSidebarOpen(false);
    };
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, [sidebarOpen, setSidebarOpen]);

  // Auto-close sidebar on route change when on mobile
  useEffect(() => {
    if (window.innerWidth < MOBILE_BREAKPOINT && sidebarOpen) {
      setSidebarOpen(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  return (
    <div
      className="shell-root"
      style={{ "--sidebar-width": `${sidebarWidth}px` } as React.CSSProperties}
    >
      <GlobalTopbar />
      {!sidebarOpen && <IconNav />}
      <AgentSidebar />
      {/* Backdrop: dims content when sidebar is open on mobile */}
      {sidebarOpen && isMobile && (
        <div
          className="shell-mobile-backdrop"
          onClick={() => setSidebarOpen(false)}
          aria-hidden="true"
        />
      )}
      <main
        className={`shell-content ${sidebarOpen ? "sidebar-open" : "sidebar-closed"}`}
      >
        {children}
      </main>
      {showTour && <GuidedTour onClose={closeTour} />}
    </div>
  );
}
